<template>
  <Teleport to="body">
    <Transition name="session-nearing-overlay">
      <div
        v-if="show"
        ref="rootEl"
        tabindex="-1"
        class="session-nearing-modal session-nearing-modal--glass"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="session-nearing-modal-title"
        aria-describedby="session-nearing-modal-desc"
        @keydown.esc.prevent="onContinue"
      >
        <div class="session-nearing-modal__backdrop session-nearing-modal__backdrop--glass" aria-hidden="true" />
        <Transition name="session-nearing-panel" appear>
          <div
            v-if="show"
            class="session-nearing-modal__panel session-nearing-modal__panel--glass"
            @click.stop
          >
            <div class="session-nearing-modal__icon-wrap session-nearing-modal__icon-wrap--glass" aria-hidden="true">
              <i class="bi bi-alarm" />
            </div>
            <h2 id="session-nearing-modal-title" class="session-nearing-modal__title">Session ending soon</h2>
            <p id="session-nearing-modal-desc" class="session-nearing-modal__text">
              About <strong class="session-nearing-modal__time">{{ liveRemaining }}</strong> left before
              your session ends<template v-if="expiresLabel"> ({{ expiresLabel }})</template>. Save your
              work, then sign in again when you are ready.
            </p>
            <div class="session-nearing-modal__actions session-nearing-modal__actions--glass">
              <PrimaryButton
                label="Continue"
                icon="bi bi-check-lg"
                @click="onContinue"
              />
              <SecondaryButton
                label="Log out"
                icon="bi bi-box-arrow-right"
                @click="onLogout"
              />
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';

function formatRemaining(ms) {
  if (ms <= 0) return '0s';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

export default {
  name: 'SessionNearingExpiryModal',
  components: {
    PrimaryButton,
    SecondaryButton,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    /** JWT expiry time (ms since epoch) for live countdown */
    expiresAtMs: {
      type: Number,
      default: null,
    },
    /** Shown once when modal opens; clock line updates from expiresAtMs */
    expiresLabel: {
      type: String,
      default: '',
    },
  },
  emits: ['continue', 'logout'],
  data() {
    return {
      tick: Date.now(),
      tickId: null,
    };
  },
  computed: {
    liveRemaining() {
      void this.tick;
      if (!this.expiresAtMs) return '—';
      const ms = Math.max(0, this.expiresAtMs - Date.now());
      return formatRemaining(ms);
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.startTick();
        this.$nextTick(() => this.$refs.rootEl?.focus?.());
      } else {
        this.stopTick();
      }
    },
  },
  beforeUnmount() {
    this.stopTick();
  },
  methods: {
    startTick() {
      this.stopTick();
      this.tick = Date.now();
      this.tickId = window.setInterval(() => {
        this.tick = Date.now();
      }, 1000);
    },
    stopTick() {
      if (this.tickId) {
        clearInterval(this.tickId);
        this.tickId = null;
      }
    },
    onContinue() {
      this.$emit('continue');
    },
    onLogout() {
      this.$emit('logout');
    },
  },
};
</script>

<style scoped src="./SessionNearingExpiryModal.css"></style>
